import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { Button, Select, Dialog, MenuItem } from '@dsi/react-eds';
import { useTestExecutionForm } from './test-execution-form';
import styles from './TestExecutionDialog.module.css';
import ReactDOM from 'react-dom';
import api from '../../api/axiosInstance';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { AxiosError } from 'axios';
import keycloak from '../../services/keycloak';

export interface TestExecutionDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (data: {
    testCollectionId: string;
    testCollectionName: string;
    workflowId: string;
    workflowDbId: string;
    autoFlowDbId: string;
    testServiceId: string;
    rAppId: string;
    productLine: string;
    username: string;
  }) => void;
}

type TestCollection = {
  id: string;
  testCollectionName: string;
  productLine?: string;
  productNumber?: string;
  productVersion?: string;
  testServiceId?: string;
  testServiceName?: string;
  rAppId?: string;
  createdAt?: string;
  updatedAt?: string;
};

const TestExecutionDialog: React.FC<TestExecutionDialogProps> = ({ open, onClose, onApply }) => {
  const {
    handleSubmit,
    formState: { isValid },
    reset,
    control,
    watch
  } = useTestExecutionForm();

  const [testCollections, setTestCollections] = useState<TestCollection[]>([]);
  const [workflowIds, setWorkflowIds] = useState<{ id: string; workflowId: string; automationWorkflowId: string }[]>([]);
  const { addNotifications } = useSwobNotifications();
  const [collectionLoader, setCollectionLoader] = useState<boolean>(false);
  const [workflowLoader, setWorkflowLoader] = useState<boolean>(false);

  const selectedTestCollection = watch('testCollection');
  const selectedCollectionData = testCollections.find((tc) => tc.testCollectionName === selectedTestCollection);

  useEffect(() => {
    if (open) {
      reset();
      // document.body.style.overflow = 'hidden';
    } else {
      //  document.body.style.overflow = '';
    }
    return () => {
      // document.body.style.overflow = '';
    };
  }, [open, reset]);

  useEffect(() => {
    const fetchTestCollections = async () => {
      try {
        setCollectionLoader(true);
        const response = await api.get('/api/test-collections');
        // Handle both array and single object responses
        const collections = Array.isArray(response.data) ? response.data : [response.data];
        setTestCollections(collections);
      } catch (error: unknown) {
        addNotifications([
          {
            title: 'Error',
            icon: 'icon-triangle-warning',
            description:
              error && error instanceof AxiosError
                ? error.response?.data?.message || error.message
                : 'An error occurred while fetching test collections',
            timestamp: new Date(),
            color: 'red'
          }
        ]);
      } finally {
        setCollectionLoader(false);
      }
    };

    if (open) {
      fetchTestCollections();
    }
  }, [open, addNotifications]);

  const handleTestCollectionChange = (testCollectionName: string) => {
    fetchWorkflowIds(testCollectionName);
  };

  const fetchWorkflowIds = async (testCollectionName: string) => {
    try {
      setWorkflowLoader(true);
      const response = await api.get(`/api/workflows/ids/success/${testCollectionName}`);
      // API returns an array of workflow ticket IDs
      const ids = Array.isArray(response.data) ? response.data : [];
      setWorkflowIds(ids);
    } catch (error: unknown) {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching workflow IDs',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
      console.error('Error fetching workflow IDs:', error);
    } finally {
      setWorkflowLoader(false);
    }
  };

  const handleFormSubmit = (data: { testCollection: string; workflowId: string }) => {
    const selectedCollection = testCollections.find((tc) => tc.testCollectionName === data.testCollection);
    const selectedWorkflow = workflowIds.find((w) => w.workflowId === data.workflowId);
    const submitData = {
      testCollectionId: selectedCollection?.id || '',
      testCollectionName: data.testCollection,
      workflowId: data.workflowId,
      workflowDbId: selectedWorkflow?.id || '',
      autoFlowDbId: selectedWorkflow?.automationWorkflowId || '',
      productLine: selectedCollection?.productLine || '',
      rAppId: selectedCollection?.rAppId || '',
      testServiceId: selectedCollection?.testServiceId || '',
      username: keycloak.tokenParsed?.preferred_username || ''
    };
    onApply(submitData);
    reset();
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  return ReactDOM.createPortal(
    <ReactEDSWrapper
      id="add-test-execution-dialog"
      component={Dialog}
      class
      show={open}
      onClose={handleCancel}
      title="Test Execution"
      bottom={
        <ReactEDSWrapper
          component={Button}
          variant="primary"
          className="btn primary"
          type="submit"
          form="test-execution-form"
          disabled={!isValid}
        >
          Apply
        </ReactEDSWrapper>
      }
    >
      <form id="test-execution-form" onSubmit={handleSubmit(handleFormSubmit)} className={styles.testExecutionForm}>
        <label htmlFor="test-collection" className={`${styles.whiteText} ${styles.testCollectionLabel}`}>
          Test Collection <span className={styles.requiredAsterisk}> *</span>
        </label>
        <Controller
          name="testCollection"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <>
              <ReactEDSWrapper
                component={Select}
                loading={collectionLoader}
                id="test-collection"
                label="Select option"
                {...field}
                onChange={(value) => {
                  const selected = Array.isArray(value) ? value[0] : value;
                  field.onChange(selected);
                  handleTestCollectionChange(selected);
                }}
                type="single"
                className={error ? `${styles.selectField} ${styles.inputError}` : styles.selectField}
                style={{ marginBottom: error ? '2px' : '16px' }}
              >
                {testCollections.map((collection) => (
                  <ReactEDSWrapper key={collection.id} component={MenuItem} value={collection.testCollectionName}>
                    {collection.testCollectionName}
                  </ReactEDSWrapper>
                ))}
              </ReactEDSWrapper>
              <div className={styles.errorContainer}>
                {error ? (
                  <span className={styles.error}>
                    <span className={styles.dangerIcon} aria-label="error" role="img">
                      &#9888;
                    </span>
                    {error.message}
                  </span>
                ) : null}
              </div>
            </>
          )}
        />

        <div className={styles.productDescription}>
          <div className={styles.productDescriptionLabel}>Product Description</div>
          {selectedCollectionData ? (
            <>
              {selectedCollectionData.productLine && (
                <div className={styles.productDescriptionValue}>{selectedCollectionData.productLine}</div>
              )}
              {selectedCollectionData.productNumber && (
                <div className={styles.productDescriptionValue}>{selectedCollectionData.productNumber}</div>
              )}
              {selectedCollectionData.productVersion && (
                <div className={styles.productDescriptionValue}>{selectedCollectionData.productVersion}</div>
              )}
            </>
          ) : (
            <>
              <div className={styles.productDescriptionItem}>Product Line</div>
              <div className={styles.productDescriptionItem}>Product Number</div>
              <div className={styles.productDescriptionItem}>Product Version</div>
            </>
          )}
        </div>

        <label htmlFor="workflow-id" className={`${styles.whiteText} ${styles.testWorkflowLabel}`}>
          Workflow ID <span className={styles.requiredAsterisk}> *</span>
        </label>
        <Controller
          name="workflowId"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <>
              <ReactEDSWrapper
                component={Select}
                id="workflow-id"
                loading={workflowLoader}
                label="Select option"
                {...field}
                onChange={(value) => {
                  const selected = Array.isArray(value) ? value[0] : value;
                  field.onChange(selected);
                }}
                type="single"
                className={error ? `${styles.selectField} ${styles.inputError}` : styles.selectField}
                style={{ marginBottom: error ? '2px' : '16px' }}
              >
                {workflowIds.map((workflowId) => (
                  <ReactEDSWrapper key={workflowId.workflowId} component={MenuItem} value={workflowId.workflowId}>
                    {workflowId.workflowId}
                  </ReactEDSWrapper>
                ))}
              </ReactEDSWrapper>
              {error ? (
                <span className={styles.error}>
                  <span className={styles.dangerIcon} aria-label="error" role="img">
                    &#9888;
                  </span>
                  {error.message}
                </span>
              ) : null}
            </>
          )}
        />
      </form>
    </ReactEDSWrapper>,
    document.body
  );
};

export default TestExecutionDialog;
